import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  dischargeAdvices: [],
  currentDischargeAdvice: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all discharge advices
export const fetchDischargeAdvices = createAsyncThunk(
  "dischargeAdvice/fetchDischargeAdvices",
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/discharge-advices/patient/${patientId}`
        : '/discharge-advices';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Discharge Advices"
      );
    }
  }
);

// Fetch single discharge advice
export const fetchDischargeAdvice = createAsyncThunk(
  "dischargeAdvice/fetchDischargeAdvice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/discharge-advices/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch Discharge Advice"
      );
    }
  }
);

// Create new discharge advice
export const createDischargeAdvice = createAsyncThunk(
  "dischargeAdvice/createDischargeAdvice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post('/discharge-advices', data);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to create Discharge Advice"
      );
    }
  }
);

// Update discharge advice
export const updateDischargeAdvice = createAsyncThunk(
  "dischargeAdvice/updateDischargeAdvice",
  async ({ case_id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/discharge-advices/case/${case_id}`, data);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422)
        return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || "Failed to update Discharge Advice"
      );
    }
  }
);

// Delete discharge advice
export const deleteDischargeAdvice = createAsyncThunk(
  "dischargeAdvice/deleteDischargeAdvice",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/discharge-advices/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete Discharge Advice"
      );
    }
  }
);

// ==================== Slice ====================
const dischargeAdviceSlice = createSlice({
  name: "dischargeAdvice",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentDischargeAdvice: (state, action) => {
      state.currentDischargeAdvice = action.payload;
    },
    clearCurrentDischargeAdvice: (state) => {
      state.currentDischargeAdvice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchDischargeAdvices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDischargeAdvices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dischargeAdvices = action.payload;
      })
      .addCase(fetchDischargeAdvices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchDischargeAdvice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDischargeAdvice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDischargeAdvice = action.payload;
      })
      .addCase(fetchDischargeAdvice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createDischargeAdvice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createDischargeAdvice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dischargeAdvices.unshift(action.payload);
      })
      .addCase(createDischargeAdvice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateDischargeAdvice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDischargeAdvice.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.dischargeAdvices.findIndex(
          (d) => d.id === updated.id
        );
        if (index !== -1) state.dischargeAdvices[index] = updated;

        if (state.currentDischargeAdvice?.id === updated.id) {
          state.currentDischargeAdvice = updated;
        }
      })
      .addCase(updateDischargeAdvice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteDischargeAdvice.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteDischargeAdvice.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dischargeAdvices = state.dischargeAdvices.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentDischargeAdvice?.id === action.payload) {
          state.currentDischargeAdvice = null;
        }
      })
      .addCase(deleteDischargeAdvice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentDischargeAdvice,
  clearCurrentDischargeAdvice,
} = dischargeAdviceSlice.actions;

export default dischargeAdviceSlice.reducer;